import React, { useState } from 'react';

import { getValueAtPath, inferType } from '../model/json-path';
import { getTypeTagStyle } from '../styles/theme';
import type { JsonKind, JsonPath, JsonValue, SafeJsonEditorTheme } from '../types';
import { AddKeyDialog } from './AddKeyDialog';

export interface FieldPanelProps {
  value: JsonValue;
  selectedPath: JsonPath | null;
  canEditStructure: boolean;
  theme: SafeJsonEditorTheme;
  onUpdatePrimitive: (path: JsonPath, nextValue: JsonValue) => void;
  onRenameKey: (path: JsonPath, nextKey: string) => void;
  onConvertType: (path: JsonPath, nextType: JsonKind) => void;
  onAddObjectKey: (path: JsonPath, key: string, keyValue: JsonValue) => void;
  onRemoveKey: (path: JsonPath) => void;
  onAddArrayItem: (path: JsonPath, index: number, position: 'above' | 'below') => void;
  onRemoveArrayItem: (path: JsonPath) => void;
  /** Called when the user clicks a nested object/array child listed in the panel — selects that path (expanding/scrolling to it in the tree, and refreshing the panel to show its own contents). */
  onNavigateToPath: (path: JsonPath) => void;
}

const pathToLabel = (path: JsonPath): string => {
  if (path.length === 0) return 'root';
  return path.map((segment) => (typeof segment === 'number' ? `[${segment}]` : segment)).join(' → ');
};

interface ChildEntry {
  key: string;
  label: string;
  path: JsonPath;
  value: JsonValue;
  type: JsonKind;
}

/**
 * The Sditor-style "conf" panel: a focused editing form for whichever node
 * is currently selected in the tree. All structural buttons here are
 * proactively shown/hidden according to the safe-edit policy — there is no
 * reactive revert-on-invalid-change anywhere, because every action this
 * panel exposes is, by construction, one the policy allows.
 */
export const FieldPanel: React.FC<FieldPanelProps> = ({
  value,
  selectedPath,
  canEditStructure,
  theme,
  onUpdatePrimitive,
  onRenameKey,
  onConvertType,
  onAddObjectKey,
  onRemoveKey,
  onAddArrayItem,
  onRemoveArrayItem,
  onNavigateToPath,
}) => {
  const [isAddKeyDialogOpen, setIsAddKeyDialogOpen] = useState(false);

  if (!selectedPath) {
    return <div className="sje-panel-empty">Select a node in the tree to edit it here.</div>;
  }

  const node = getValueAtPath(value, selectedPath);
  const nodeType = inferType(node);
  const isRoot = selectedPath.length === 0;
  const keySegment = selectedPath[selectedPath.length - 1];
  const isArrayItem = typeof keySegment === 'number';
  const keyLabel = isRoot ? 'root' : isArrayItem ? `[${keySegment}]` : String(keySegment);
  const existingKeys = nodeType === 'object' ? Object.keys((node as Record<string, JsonValue>) ?? {}) : [];

  const children: ChildEntry[] =
    nodeType === 'object'
      ? Object.entries(node as Record<string, JsonValue>).map(([childKey, childValue]) => ({
          key: childKey,
          label: childKey,
          path: [...selectedPath, childKey],
          value: childValue,
          type: inferType(childValue),
        }))
      : nodeType === 'array'
        ? (node as JsonValue[]).map((childValue, index) => ({
            key: String(index),
            label: `[${index}]`,
            path: [...selectedPath, index],
            value: childValue,
            type: inferType(childValue),
          }))
        : [];

  const renderChildControl = (child: ChildEntry): React.ReactNode => {
    if (child.type === 'string') {
      return (
        <input
          className="sje-panel-input sje-panel-child-control"
          placeholder={child.label}
          value={typeof child.value === 'string' ? child.value : ''}
          onChange={(event) => onUpdatePrimitive(child.path, event.target.value)}
        />
      );
    }

    if (child.type === 'number') {
      return (
        <input
          type="number"
          className="sje-panel-input sje-panel-child-control"
          placeholder={child.label}
          value={typeof child.value === 'number' ? child.value : 0}
          onChange={(event) => onUpdatePrimitive(child.path, Number(event.target.value))}
        />
      );
    }

    if (child.type === 'boolean') {
      return (
        <select
          className="sje-panel-select sje-panel-child-control"
          value={String(Boolean(child.value))}
          onChange={(event) => onUpdatePrimitive(child.path, event.target.value === 'true')}
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    return <span className="sje-panel-child-null">null</span>;
  };

  return (
    <div className="sje-panel">
      <div className="sje-panel-breadcrumb">{pathToLabel(selectedPath)}</div>

      {!isRoot && !isArrayItem ? (
        <div className="sje-panel-field">
          <label className="sje-panel-label" htmlFor="sje-key-input">
            Key
          </label>
          {canEditStructure ? (
            <input
              id="sje-key-input"
              className="sje-panel-input"
              defaultValue={keyLabel}
              onBlur={(event) => onRenameKey(selectedPath, event.target.value)}
            />
          ) : (
            <div>{keyLabel}</div>
          )}
        </div>
      ) : null}

      <div className="sje-panel-field">
        <label className="sje-panel-label" htmlFor="sje-type-select">
          Type
        </label>
        {canEditStructure && !isRoot ? (
          <select
            id="sje-type-select"
            className="sje-panel-select"
            value={nodeType}
            onChange={(event) => onConvertType(selectedPath, event.target.value as JsonKind)}
          >
            <option value="object">object</option>
            <option value="array">array</option>
            <option value="string">string</option>
            <option value="number">number</option>
            <option value="boolean">boolean</option>
            <option value="null">null</option>
          </select>
        ) : (
          <span className="sje-row-type-tag" style={getTypeTagStyle(theme, nodeType)}>
            {nodeType}
          </span>
        )}
      </div>

      {nodeType === 'string' ? (
        <div className="sje-panel-field">
          <label className="sje-panel-label" htmlFor="sje-value-input">
            Value
          </label>
          <textarea
            id="sje-value-input"
            className="sje-panel-input sje-panel-textarea"
            rows={8}
            placeholder={keyLabel}
            value={typeof node === 'string' ? node : ''}
            onChange={(event) => onUpdatePrimitive(selectedPath, event.target.value)}
          />
        </div>
      ) : null}

      {nodeType === 'number' ? (
        <div className="sje-panel-field">
          <label className="sje-panel-label" htmlFor="sje-value-input">
            Value
          </label>
          <input
            id="sje-value-input"
            type="number"
            className="sje-panel-input"
            placeholder={keyLabel}
            value={typeof node === 'number' ? node : 0}
            onChange={(event) => onUpdatePrimitive(selectedPath, Number(event.target.value))}
          />
        </div>
      ) : null}

      {nodeType === 'boolean' ? (
        <div className="sje-panel-field">
          <label className="sje-panel-label" htmlFor="sje-value-select">
            Value
          </label>
          <select
            id="sje-value-select"
            className="sje-panel-select"
            value={String(Boolean(node))}
            onChange={(event) => onUpdatePrimitive(selectedPath, event.target.value === 'true')}
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        </div>
      ) : null}

      {nodeType === 'null' ? (
        <div className="sje-panel-field">
          <label className="sje-panel-label">Value</label>
          <div>null</div>
        </div>
      ) : null}

      {nodeType === 'object' || nodeType === 'array' ? (
        <div className="sje-panel-field">
          <label className="sje-panel-label">{nodeType === 'object' ? 'Keys' : 'Items'}</label>
          {children.length === 0 ? (
            <div className="sje-panel-child-empty">{nodeType === 'object' ? 'No keys yet.' : 'No items yet.'}</div>
          ) : (
            <div className="sje-panel-child-list">
              {children.map((child) => {
                const isContainer = child.type === 'object' || child.type === 'array';
                return (
                  <div
                    className={isContainer ? 'sje-panel-child-row sje-panel-child-row-nav' : 'sje-panel-child-row'}
                    key={child.key}
                  >
                    {isContainer ? (
                      <button type="button" className="sje-panel-child-nav" onClick={() => onNavigateToPath(child.path)}>
                        <span className="sje-panel-child-nav-name" title={child.label}>
                          {child.label}
                        </span>
                        <span className="sje-row-type-tag" style={getTypeTagStyle(theme, child.type)}>
                          {child.type}
                        </span>
                        <span className="sje-panel-child-nav-count">
                          {child.type === 'object'
                            ? `${Object.keys((child.value as Record<string, JsonValue>) ?? {}).length} keys`
                            : `${(child.value as JsonValue[]).length} items`}
                        </span>
                        <span aria-hidden="true">→</span>
                      </button>
                    ) : (
                      <>
                        <span className="sje-panel-child-label" title={child.label}>
                          {child.label}
                        </span>
                        {renderChildControl(child)}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      <div className="sje-panel-actions">
        {nodeType === 'object' ? (
          <button type="button" className="sje-panel-button" onClick={() => setIsAddKeyDialogOpen(true)}>
            Add key
          </button>
        ) : null}

        {!isRoot && !isArrayItem && canEditStructure ? (
          <button
            type="button"
            className="sje-panel-button sje-panel-button-danger"
            onClick={() => onRemoveKey(selectedPath)}
          >
            Remove key
          </button>
        ) : null}

        {nodeType === 'array' && (node as JsonValue[]).length === 0 && canEditStructure ? (
          <button type="button" className="sje-panel-button" onClick={() => onAddArrayItem(selectedPath, 0, 'below')}>
            Add item
          </button>
        ) : null}

        {isArrayItem ? (
          <>
            <button
              type="button"
              className="sje-panel-button"
              onClick={() => onAddArrayItem(selectedPath.slice(0, -1), keySegment as number, 'above')}
            >
              Add above
            </button>
            <button
              type="button"
              className="sje-panel-button"
              onClick={() => onAddArrayItem(selectedPath.slice(0, -1), keySegment as number, 'below')}
            >
              Add below
            </button>
            <button
              type="button"
              className="sje-panel-button sje-panel-button-danger"
              onClick={() => onRemoveArrayItem(selectedPath)}
            >
              Remove item
            </button>
          </>
        ) : null}
      </div>

      {isAddKeyDialogOpen ? (
        <AddKeyDialog
          existingKeys={existingKeys}
          onCancel={() => setIsAddKeyDialogOpen(false)}
          onConfirm={(key, keyValue) => {
            onAddObjectKey(selectedPath, key, keyValue);
            setIsAddKeyDialogOpen(false);
          }}
        />
      ) : null}
    </div>
  );
};
